import { create, updateField, updateFields, update, remove, getById, getAll, getBySingleQuery } from '../utils/dbUtils';
import { isBooleanFalse, isBooleanTrue, isListEmpty, isListNotEmpty, isNotNullOrUndefined, isNullOrUndefined, isObjectNotEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from "../utils/objectUtils";
import { collection, getCountFromServer, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import AuditLog from './AuditLog';
import moment from 'moment-timezone';

class ConversationMessage {
    static collectionName = 'conversationMessages';

    conversationId = null; // conversationMessage Id
    authorId = null; // user Id of sender
    type = 'TEXT'; // TEXT, MEDIA, etc.
    sentAt = moment().utc().toDate(); // timestamp

    content = {
        raw: '',           // Raw text content
        richText: null,    // Serialized rich text content
        mentions: [],      // Array of { userId, username, indices: [start, end] }
        hashtags: []       // Array of { tag, indices: [start, end] }
    };

    fileAttachments = []; // Array of { url, refId, type, name, size, metadata }
    readReceipts = [];    // Array of { userId, readAt }
    reactions = {};       // Map of reaction types to arrays of userIds
    replies = [];         // Array of reply message IDs
    status = 'SENT';      // DRAFT, SENT, DELETED   

    metadata = {
        authorName: null,
        authorPhotoUrl: null
    };

    testMode = false;

    constructor(message) {
        if (isObjectNotEmpty(message)) {
            if (isStringNotEmpty(message.conversationId)) { this.conversationId = message.conversationId; }
            if (isStringNotEmpty(message.authorId)) { this.authorId = message.authorId; }
            if (isStringNotEmpty(message.type)) { this.type = message.type; }
            if (isNotNullOrUndefined(message.sentAt)) { this.sentAt = message.sentAt; }

            if (isObjectNotEmpty(message.content)) {
                this.content = {
                    raw: message.content.raw || '',
                    richText: message.content.richText || null,
                    mentions: message.content.mentions || [],
                    hashtags: message.content.hashtags || []
                };
            }

            if (isListNotEmpty(message.fileAttachments)) { this.fileAttachments = message.fileAttachments; }

            this.readReceipts = message.readReceipts || [];
            this.reactions = message.reactions || {};
            this.replies = message.replies || [];
            this.status = message.status || 'SENT';

            if (isObjectNotEmpty(message.metadata)) {
                this.metadata = {
                    authorName: message.metadata.authorName || null,
                    authorPhotoUrl: message.metadata.authorPhotoUrl || null
                };
            }

            if (isBooleanTrue(message.testMode)) { this.testMode = true; }
        }
    }

    toDto = () => {
        return {
            conversationId: this.conversationId,
            authorId: this.authorId,
            type: this.type,
            sentAt: this.sentAt,
            content: this.content,
            fileAttachments: this.fileAttachments,
            readReceipts: this.readReceipts,
            reactions: this.reactions,
            replies: this.replies,
            status: this.status,
            metadata: this.metadata,
            testMode: this.testMode
        };
    };

    isValid = () => {
        if (isNullOrUndefined(this.conversationId)) {
            throw new Error('Message requires conversationId');
        }
        if (isNullOrUndefined(this.authorId)) {
            throw new Error('Message requires authorId');
        }
        if (isNullOrUndefined(this.sentAt)) {
            throw new Error('Message requires sentAt');
        }
        if (isNullOrUndefined(this.content.raw)) {
            throw new Error('Message requires content');
        }

        return true;
    }

    create = async () => {
        if (this.isValid()) {
            return await create(ConversationMessage.collectionName, this.toDto());
        }
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(ConversationMessage.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static updateFields = async (id, updatedFields) => {
        if (isStringNotEmpty(id)) {
            return await updateFields(ConversationMessage.collectionName, id, updatedFields);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedConversation) => {
        const updatedObj = new ConversationMessage(updatedConversation);
        if (updatedObj.isValid() && id === updatedConversation.id) {
            return await update(ConversationMessage.collectionName, id, updatedObj.toDto());
        } else {
            throw new Error('id and id field of the updated message must match.')
        }
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            const message = await getById(ConversationMessage.collectionName, id)
            const auditLog = new AuditLog({
                type: "conversationMessage",
                action: "delete",
                state: message.toDto(),
                testMode: isBooleanTrue(message.testMode)
            })
            await auditLog.create()
            return await remove(ConversationMessage.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(ConversationMessage.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getAll = async () => {
        return await getAll(ConversationMessage.collectionName);
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(ConversationMessage.collectionName, fieldName, fieldValue, comparer);
    }

    static getByAuthorId = async (authorId) => {
        return await getBySingleQuery(ConversationMessage.collectionName, 'authorId', authorId, '==');
    }

    static getByConversationId = async (conversationId) => {
        return await this.getPaginated({ conversationId }, 25, 'sentAt')
    }

    static addReaction = async (messageId, reactionType, userId) => {
        const message = await this.getById(messageId);
        const reactions = message.reactions || {};

        if (!reactions[reactionType]) {
            reactions[reactionType] = [];
        }

        if (!reactions[reactionType].includes(userId)) {
            reactions[reactionType].push(userId);
            await this.updateField(messageId, 'reactions', reactions);
        }
    }

    static removeReaction = async (messageId, reactionType, userId) => {
        const message = await this.getById(messageId);
        const reactions = message.reactions || {};

        if (reactions[reactionType]) {
            reactions[reactionType] = reactions[reactionType].filter(id => id !== userId);
            if (reactions[reactionType].length === 0) {
                delete reactions[reactionType];
            }
            await this.updateField(messageId, 'reactions', reactions);
        }
    }

    static markAsRead = async (messageId, userId) => {
        const message = await this.getById(messageId);
        const readReceipts = message.readReceipts || [];

        if (!readReceipts.find(receipt => receipt.userId === userId)) {
            readReceipts.push({
                userId,
                readAt: moment().utc().toDate()
            });
            await this.updateField(messageId, 'readReceipts', readReceipts);
        }
    }

    static addReply = async (messageId, replyMessageId) => {
        const message = await this.getById(messageId);
        const replies = message.replies || [];

        if (!replies.includes(replyMessageId)) {
            replies.push(replyMessageId);
            await this.updateField(messageId, 'replies', replies);
        }
    }

    static getCount = async (queryParameters) => {
        const { testMode, conversationId } = queryParameters;

        let queryConstraints = [];

        if (isNotNullOrUndefined(testMode)) {
            queryConstraints.push(where('testMode', '==', isBooleanTrue(testMode)));
        }

        if (isStringNotEmptyOrZeroLength(conversationId)) {
            queryConstraints.push(where('conversationId', '==', conversationId));
        }

        let q = query(collection(db, ConversationMessage.collectionName), ...queryConstraints);
        const snapShot = await getCountFromServer(q);
        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters, pageSize = 25, orderByField = 'sentAt') => {
        const { testMode, conversationId } = queryParameters;

        let queryConstraints = [];

        if (isNotNullOrUndefined(testMode)) {
            queryConstraints.push(where('testMode', '==', isBooleanTrue(testMode)));
        }

        if (isStringNotEmptyOrZeroLength(conversationId)) {
            queryConstraints.push(where('conversationId', '==', conversationId));
        }

        const q = query(
            collection(db, ConversationMessage.collectionName),
            ...queryConstraints,
            orderBy(orderByField, 'desc'),
            limit(pageSize)
        );

        let snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data()
        }));
    }

    static get = async (queryParameters) => {
        let allRecords = [];
        try {
            let totalCount = await ConversationMessage.getCount(queryParameters);
            let totalPages = Math.ceil(totalCount / 10000.0);

            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }

            for (const page of pages) {
                let list = await ConversationMessage.getPaginated(queryParameters, 10000, 'sentAt');
                allRecords = [...allRecords, ...list];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }
};

export default ConversationMessage;